/**
 * Theme Configuration for Personality Card System
 * 
 * This file defines color palettes, typography, and styling for:
 * - 4 age groups (Kids, Teens, Young Adults, Mature)
 * - 5 personas per card type (15 total personas across 3 card types)
 * - 3 card types (Brain/Personality, Character, Growth/Advice)
 */

// ============================================================================
// COLOR PALETTES BY PERSONA TYPE
// ============================================================================

/**
 * CARD TYPE 1: Brain & Personality Analysis
 * Framework: The Analyst, The Empath, The Visionary, The Guardian, The Explorer
 */
export const personalityThemes = {
  analyst: {
    name: 'The Analyst',
    primary: '#2563eb', // Royal Blue
    secondary: '#1e40af',
    accent: '#60a5fa',
    background: 'from-blue-50 to-indigo-100',
    text: '#1e3a8a',
    icon: '🧠',
    traits: ['Logical', 'Strategic', 'Data-driven', 'Systematic'],
  },
  empath: {
    name: 'The Empath',
    primary: '#ec4899', // Pink
    secondary: '#db2777',
    accent: '#f9a8d4',
    background: 'from-pink-50 to-rose-100',
    text: '#9f1239',
    icon: '❤️‍🔥',
    traits: ['Caring', 'Intuitive', 'Supportive', 'Compassionate'],
  },
  visionary: {
    name: 'The Visionary',
    primary: '#8b5cf6', // Purple
    secondary: '#7c3aed',
    accent: '#c4b5fd',
    background: 'from-purple-50 to-violet-100',
    text: '#5b21b6',
    icon: '🔭',
    traits: ['Creative', 'Imaginative', 'Innovative', 'Forward-thinking'],
  },
  guardian: {
    name: 'The Guardian',
    primary: '#059669', // Green
    secondary: '#047857',
    accent: '#6ee7b7',
    background: 'from-green-50 to-emerald-100',
    text: '#064e3b',
    icon: '⚓',
    traits: ['Reliable', 'Organized', 'Traditional', 'Protective'],
  },
  explorer: {
    name: 'The Explorer',
    primary: '#f59e0b', // Amber
    secondary: '#d97706',
    accent: '#fcd34d',
    background: 'from-amber-50 to-orange-100',
    text: '#92400e',
    icon: '🪐',
    traits: ['Adventurous', 'Curious', 'Spontaneous', 'Bold'],
  },
};

/**
 * CARD TYPE 2: Character & Strengths/Weaknesses
 * Framework: Energy-based personalities
 */
export const characterThemes = {
  highEnergyExtrovert: {
    name: 'High Energy Extrovert',
    primary: '#ef4444', // Red
    secondary: '#dc2626',
    accent: '#fca5a5',
    background: 'from-red-50 to-orange-100',
    text: '#991b1b',
    icon: '🔥',
    traits: ['Energetic', 'Outgoing', 'Dynamic', 'Enthusiastic'],
  },
  balancedAmbivert: {
    name: 'Balanced Ambivert',
    primary: '#06b6d4', // Cyan
    secondary: '#0891b2',
    accent: '#67e8f9',
    background: 'from-cyan-50 to-teal-100',
    text: '#134e4a',
    icon: '☯️',
    traits: ['Adaptable', 'Flexible', 'Balanced', 'Versatile'],
  },
  reflectiveIntrovert: {
    name: 'Reflective Introvert',
    primary: '#6366f1', // Indigo
    secondary: '#4f46e5',
    accent: '#a5b4fc',
    background: 'from-indigo-50 to-blue-100',
    text: '#312e81',
    icon: '🌙',
    traits: ['Thoughtful', 'Deep', 'Introspective', 'Calm'],
  },
  dynamicCreative: {
    name: 'Dynamic Creative',
    primary: '#a855f7', // Fuchsia
    secondary: '#9333ea',
    accent: '#e9d5ff',
    background: 'from-fuchsia-50 to-purple-100',
    text: '#701a75',
    icon: '🌟',
    traits: ['Artistic', 'Original', 'Expressive', 'Passionate'],
  },
  steadyReliable: {
    name: 'Steady Reliable',
    primary: '#14b8a6', // Teal
    secondary: '#0d9488',
    accent: '#5eead4',
    background: 'from-teal-50 to-cyan-100',
    text: '#115e59',
    icon: '🏔️',
    traits: ['Dependable', 'Consistent', 'Grounded', 'Trustworthy'],
  },
};

/**
 * CARD TYPE 3: Growth & Self-Improvement
 * Framework: Career/Purpose-oriented
 */
export const growthThemes = {
  thinker: {
    name: 'The Thinker',
    primary: '#0ea5e9', // Sky Blue
    secondary: '#0284c7',
    accent: '#7dd3fc',
    background: 'from-sky-50 to-blue-100',
    text: '#075985',
    icon: '♟️',
    traits: ['Analytical', 'Researcher', 'Problem-solver', 'Strategic'],
    careerPaths: ['Scientist', 'Analyst', 'Engineer', 'Researcher'],
  },
  creator: {
    name: 'The Creator',
    primary: '#f97316', // Orange
    secondary: '#ea580c',
    accent: '#fed7aa',
    background: 'from-orange-50 to-amber-100',
    text: '#9a3412',
    icon: '🪄',
    traits: ['Artistic', 'Innovative', 'Designer', 'Maker'],
    careerPaths: ['Artist', 'Designer', 'Writer', 'Entrepreneur'],
  },
  leader: {
    name: 'The Leader',
    primary: '#dc2626', // Red
    secondary: '#b91c1c',
    accent: '#fecaca',
    background: 'from-red-50 to-rose-100',
    text: '#7f1d1d',
    icon: '👑',
    traits: ['Influential', 'Decisive', 'Visionary', 'Motivator'],
    careerPaths: ['Manager', 'CEO', 'Coach', 'Director'],
  },
  helper: {
    name: 'The Helper',
    primary: '#10b981', // Emerald
    secondary: '#059669',
    accent: '#6ee7b7',
    background: 'from-emerald-50 to-green-100',
    text: '#065f46',
    icon: '🌱',
    traits: ['Supportive', 'Caring', 'Service-oriented', 'Empathetic'],
    careerPaths: ['Teacher', 'Nurse', 'Counselor', 'Social Worker'],
  },
  doer: {
    name: 'The Doer',
    primary: '#64748b', // Slate
    secondary: '#475569',
    accent: '#cbd5e1',
    background: 'from-slate-50 to-gray-100',
    text: '#1e293b',
    icon: '🚀',
    traits: ['Practical', 'Hands-on', 'Technical', 'Results-focused'],
    careerPaths: ['Builder', 'Technician', 'Operator', 'Specialist'],
  },
};

// ============================================================================
// AGE GROUP CONFIGURATIONS
// ============================================================================

export const ageGroups = {
  kids: {
    range: '6-12',
    label: 'Kids',
    fontFamily: 'Comic Sans MS, Fredoka, sans-serif',
    fontSize: {
      title: 'text-2xl',
      subtitle: 'text-base',
      body: 'text-sm',
    },
    spacing: 'space-y-3',
    iconSize: 'text-4xl',
    borderRadius: 'rounded-2xl',
    shadowSize: 'shadow-lg',
    language: {
      encouragement: 'You\'re amazing!',
      tone: 'playful',
      vocabulary: 'simple',
    },
  },
  teens: {
    range: '13-17',
    label: 'Teens',
    fontFamily: 'Quicksand, Poppins, sans-serif',
    fontSize: {
      title: 'text-xl',
      subtitle: 'text-base',
      body: 'text-sm',
    },
    spacing: 'space-y-2.5',
    iconSize: 'text-3xl',
    borderRadius: 'rounded-xl',
    shadowSize: 'shadow-md',
    language: {
      encouragement: 'Keep growing!',
      tone: 'friendly',
      vocabulary: 'conversational',
    },
  },
  youngAdults: {
    range: '18-35',
    label: 'Young Adults',
    fontFamily: 'Inter, Roboto, sans-serif',
    fontSize: {
      title: 'text-xl',
      subtitle: 'text-base',
      body: 'text-sm',
    },
    spacing: 'space-y-2',
    iconSize: 'text-2xl',
    borderRadius: 'rounded-lg',
    shadowSize: 'shadow-md',
    language: {
      encouragement: 'Embrace your potential',
      tone: 'professional',
      vocabulary: 'moderate',
    },
  },
  mature: {
    range: '36+',
    label: 'Mature Adults',
    fontFamily: 'Georgia, Merriweather, serif',
    fontSize: {
      title: 'text-xl',
      subtitle: 'text-base',
      body: 'text-sm',
    },
    spacing: 'space-y-2',
    iconSize: 'text-2xl',
    borderRadius: 'rounded-lg',
    shadowSize: 'shadow-lg',
    language: {
      encouragement: 'Wisdom and experience',
      tone: 'respectful',
      vocabulary: 'sophisticated',
    },
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get theme for Card Type 1 (Personality)
 */
/**
 * Get theme for Card Type 1 (Personality)
 */
export const getPersonalityTheme = (persona) => {
  if (!personalityThemes[persona]) {
    console.warn(`Unknown Personality persona: "${persona}". Falling back to "analyst".`);
    return personalityThemes.analyst;
  }
  return personalityThemes[persona];
};

/**
 * Get theme for Card Type 2 (Character)
 */
export const getCharacterTheme = (persona) => {
  if (!characterThemes[persona]) {
    console.warn(`Unknown Character persona: "${persona}". Falling back to "balancedAmbivert".`);
    return characterThemes.balancedAmbivert;
  }
  return characterThemes[persona];
};

/**
 * Get theme for Card Type 3 (Growth)
 */
export const getGrowthTheme = (persona) => {
  if (!growthThemes[persona]) {
    console.warn(`Unknown Growth persona: "${persona}". Falling back to "thinker".`);
    return growthThemes.thinker;
  }
  return growthThemes[persona];
};

/**
 * Get age group configuration
 */
export const getAgeGroupConfig = (age) => {
  if (age >= 6 && age <= 12) return ageGroups.kids;
  if (age >= 13 && age <= 17) return ageGroups.teens;
  if (age >= 18 && age <= 35) return ageGroups.youngAdults;
  return ageGroups.mature;
};

/**
 * Determine age group key from age number
 */
export const getAgeGroupKey = (age) => {
  if (age >= 6 && age <= 12) return 'kids';
  if (age >= 13 && age <= 17) return 'teens';
  if (age >= 18 && age <= 35) return 'youngAdults';
  return 'mature';
};

// ============================================================================
// EXPORT ALL
// ============================================================================

const themeConfig = {
  personalityThemes,
  characterThemes,
  growthThemes,
  ageGroups,
  getPersonalityTheme,
  getCharacterTheme,
  getGrowthTheme,
  getAgeGroupConfig,
  getAgeGroupKey,
};

export default themeConfig;
